import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';
import './Auth.css';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  // Get email from navigation state
  const email = location.state?.email;

  useEffect(() => {
    if (!email) {
      navigate('/register');
      return;
    }

    let interval;
    if (timer > 0 && !canResend) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0 && !canResend) {
      setCanResend(true);
    }

    return () => clearInterval(interval);
  }, [timer, canResend, email, navigate]);

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return;

    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    // Move to next input if current field is filled
    if (element.value && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Move to previous input on backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const newOtp = [...otp];
      newOtp[index - 1] = '';
      setOtp(newOtp);
      e.target.previousSibling.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      setError('Please enter all digits of the OTP');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      await authService.verifyOTP({
        email: email,
        otp: otpString
      });

      navigate('/login', { 
        state: { 
          message: 'Account verified successfully! Please login to continue.' 
        }
      });
    } catch (error) {
      setError(error.message || 'Invalid OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Use forgot-password endpoint to resend OTP
      await authService.forgotPassword(email);

      setTimer(30);
      setCanResend(false);
      setError('');
    } catch (error) {
      setError(error.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        <div className="auth-form-container">
          <div className="auth-header">
            <h2>Verify OTP</h2>
            <p>Please enter the 6-digit code sent to your email</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="otp-message">
              <p>We've sent a verification code to:</p>
              <p><strong>Email:</strong> {email}</p>
            </div>

            <div className="otp-input">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(e.target, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && (
              <div className="error-message submit-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="auth-button"
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <div className="auth-footer">
              {canResend ? (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  className="forgot-password"
                  disabled={isLoading}
                >
                  Resend OTP
                </button>
              ) : (
                <p>Resend OTP in {timer} seconds</p>
              )}
            </div>
          </form>
        </div>
        <div className="auth-graphic verify-otp">
          {/* OTP verification illustration from Freepik */}
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP; 